import './header.scss';

import { Avatar, Dropdown, type MenuProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { DownOutlined } from '@/media/antd-icons';
import BurgerMenuIcon from '@/media/icons/burger-menu.svg?react';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import Logo from '@/media/logo.svg?react';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ADMIN_ROUTES, ROUTES, getFullAdminRoute } from '@/shared/routes';
import { removeUserToken } from '@/shared/services/user-hash';
import { LocalizedLink, LocalizedNavLink } from '@/shared/ui';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsAuth } from '@/store/auth.slice';
import { resetUserInfo, setSidebarExpanded } from '@/store/user-info.slice';

const Header: React.FC = () => {
  const { navigate } = useAppNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { i18n } = useTranslation();

  const { menu, isSidebarExpanded, userInfo, userAccesses } = useAppSelector(
    (state) => state.userInfo
  );
  const { headerConfig } = useAppSelector((state) => state.app);

  const user = {
    firstName: userInfo?.first_name || 'Менеджер',
    lastName: userInfo?.last_name || 'Лизинга'
  };

  const items: MenuProps['items'] = [
    ...(userAccesses.global.isAdmin
      ? [
          {
            key: getFullAdminRoute(ADMIN_ROUTES.users),
            label: (
              <LocalizedLink
                className="header-dropdown-item"
                to={getFullAdminRoute(ADMIN_ROUTES.users)}
              >
                Панель администратора
              </LocalizedLink>
            )
          }
        ]
      : []),
    {
      key: 'logout',
      label: <p className="header-dropdown-item">Выйти</p>
    }
  ];

  const isAdminRoute = location.pathname.includes(ADMIN_ROUTES.root);

  const onClickItems: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      removeUserToken();
      dispatch(setIsAuth(false));
      dispatch(resetUserInfo());
      navigate(ROUTES.login);
    } else {
      navigate(`/${key}`);
    }
  };

  return (
    <header className="header">
      <div className="header__container">
        <button
          onClick={() => dispatch(setSidebarExpanded(true))}
          className="header__burger"
        >
          <BurgerMenuIcon />
        </button>

        {isAdminRoute ? (
          <div
            className={`header__title-wrapper header__title-wrapper--admin ${headerConfig.hasBackArrow ? 'header__title-wrapper--clickable' : ''}`}
            onClick={() => {
              if (!headerConfig.hasBackArrow) return;
              if (headerConfig.backArrowTo && headerConfig.backArrowTo !== -1) {
                navigate(headerConfig.backArrowTo);
              } else {
                navigate(-1);
              }
            }}
          >
            {headerConfig.hasBackArrow && <ChevronDownIcon />}
            <div>
              <h2 className="header__title">{headerConfig.title}</h2>
              {headerConfig.hasBackArrow && (
                <p className="header__secondary-text">
                  {headerConfig.subtitle || 'Назад'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {!isSidebarExpanded && (
              <div className="header__logo">
                <Logo />
              </div>
            )}

            <div
              className={`header__title-wrapper ${headerConfig.hasBackArrow ? 'header__title-wrapper--clickable' : ''}`}
              onClick={() => {
                if (!headerConfig.hasBackArrow) return;
                if (
                  headerConfig.backArrowTo &&
                  headerConfig.backArrowTo !== -1
                ) {
                  navigate(headerConfig.backArrowTo);
                } else {
                  navigate(-1);
                }
              }}
            >
              {headerConfig.hasBackArrow && <ChevronDownIcon />}
              <div>
                <h2 className="header__title">{headerConfig.title}</h2>
                {headerConfig.hasBackArrow && (
                  <p className="header__secondary-text">
                    {headerConfig.subtitle || 'Назад'}
                  </p>
                )}
              </div>
            </div>

            <nav className="header__main-menu">
              {menu.map((element) => {
                const isMainPage = element.code === 'main';
                const href = isMainPage ? ROUTES.home : `/${element.code}`;
                const localizedHref = `/${i18n.language}${href}`;
                const isActive =
                  location.pathname === localizedHref ||
                  (!isMainPage &&
                    location.pathname.startsWith(`${localizedHref}/`));

                const linkClasses = [
                  'header__link',
                  isActive ? 'header__link--active' : ''
                ].join(' ');

                return (
                  <LocalizedNavLink
                    to={href}
                    className={linkClasses}
                    key={element.uuid}
                  >
                    <span className="header__link-text">{element?.name}</span>
                  </LocalizedNavLink>
                );
              })}
            </nav>
          </>
        )}

        <div className="header__pagination">
          <Dropdown
            menu={{ items, onClick: onClickItems }}
            placement="bottomRight"
            arrow
          >
            <Space className="header__user">
              <Avatar className="header__user-avatar">
                {user.firstName[0]?.toUpperCase()}
                {user.lastName[0]?.toUpperCase()}
              </Avatar>{' '}
              <p className="header__user-name">
                {user.firstName} {user.lastName} <br />
              </p>
              <DownOutlined />
            </Space>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
